// All requests go to the single Python API service, proxied by Caddy
// under /api. Survey, review and upload endpoints all live here now
// (PostgREST has been removed).
const API_BASE = process.env.VUE_APP_API_BASE || '/api'

const SESSION_KEY = 'iopic_survey_session'

// -------------------------------------------------------------------
// Session helpers — persist the server-issued session token across
// page refreshes. The token authorizes every save/submit/upload and
// doubles as the survey's unique ID (S3 folder name).
// -------------------------------------------------------------------

export function saveSession (token, page = 0) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, page }))
  } catch {
    // localStorage unavailable (private browsing, quota, etc.)
  }
}

export function loadSession () {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const { token, page } = JSON.parse(raw)
    if (typeof token === 'string' && token.length > 0) {
      return { token, page: Number.isInteger(page) ? page : 0 }
    }
  } catch { /* ignore */ }
  return null
}

export function clearSession () {
  try { localStorage.removeItem(SESSION_KEY) } catch { /* ignore */ }
}

// -------------------------------------------------------------------
// HTTP helper — returns parsed JSON, or null on non-2xx / network error
// (except where callers need to distinguish errors, e.g. presign).
// -------------------------------------------------------------------

async function apiFetch (path, options = {}) {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    })
    if (!response.ok) {
      if (response.status !== 404) {
        console.error(`[api] ${options.method || 'GET'} ${path} → ${response.status}`)
      }
      return null
    }
    if (response.status === 204) return {}
    const text = await response.text()
    return text ? JSON.parse(text) : null
  } catch (err) {
    console.error('[api] fetch failed:', err)
    return null
  }
}

// -------------------------------------------------------------------
// Survey session API — all writes are gated behind the session token.
// -------------------------------------------------------------------

/**
 * Code path: validate an organisation access code. Returns
 * { name, contact_name, contact_email, region, token, resubmission }
 * (flattened for backward compatibility) or null if not recognized.
 */
export async function validateOrganisation (code) {
  const result = await apiFetch('/organisation/validate', {
    method: 'POST',
    body: JSON.stringify({ code })
  })
  if (!result || !result.token) return null
  return {
    ...(result.organisation || {}),
    token: result.token,
    resubmission: !!result.resubmission
  }
}

/** Anonymous path (no code): open a fresh session and return its token. */
export async function startSurvey () {
  const result = await apiFetch('/survey/start', { method: 'POST', body: '{}' })
  return result && result.token ? result.token : null
}

/** Fetch saved data for an open session (resume after refresh). */
export async function getTempSurvey (token) {
  const result = await apiFetch(`/survey/${encodeURIComponent(token)}`, { method: 'GET' })
  return result && typeof result.json_data === 'object' ? result.json_data : null
}

/** Save partial survey data for the current session. */
export async function saveTempSurvey (token, jsonData) {
  await apiFetch(`/survey/${encodeURIComponent(token)}`, {
    method: 'PUT',
    body: JSON.stringify({ json_data: jsonData })
  })
}

/** Persist the list of uploaded files for the current session. */
export async function setUploadedFiles (token, files) {
  await apiFetch(`/survey/${encodeURIComponent(token)}/uploaded-files`, {
    method: 'POST',
    body: JSON.stringify({ files })
  })
}

/** Finalize: insert into the permanent table and close the session. */
export async function completeSurvey (token, jsonData, files = []) {
  await apiFetch(`/survey/${encodeURIComponent(token)}/complete`, {
    method: 'POST',
    body: JSON.stringify({ json_data: jsonData, files })
  })
}

// -------------------------------------------------------------------
// Review — read a finalized submission and add files/comments to it.
// -------------------------------------------------------------------

/**
 * Fetch a finalized submission for the review page. Returns
 * { json_data, uploaded_files, additions, organisation_name,
 *   contact_email, resubmission, created_at } or null.
 */
export async function getSubmissionReview (token) {
  const result = await apiFetch(`/review/${encodeURIComponent(token)}`, { method: 'GET' })
  return result && typeof result === 'object' ? result : null
}

/** Append files and/or a comment to an already-submitted record.
 *  Throws on failure so the UI can surface the real error (not silent). */
export async function appendToSubmission (token, files = [], comment = null) {
  const response = await fetch(`${API_BASE}/review/${encodeURIComponent(token)}/additions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files, comment })
  })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try { detail = (await response.json()).detail || detail } catch { /* ignore */ }
    throw new Error(detail)
  }
}

// -------------------------------------------------------------------
// File upload — S3 presigned URLs (same API service).
// -------------------------------------------------------------------

/**
 * Request a presigned PUT URL for a file. Returns { url, key, method,
 * headers }. Object key layout: uploads/<token>/<filename>.
 */
export async function presignUpload (token, filename, contentType, size) {
  const response = await fetch(`${API_BASE}/uploads/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, filename, content_type: contentType, size })
  })
  if (!response.ok) {
    let detail = `HTTP ${response.status}`
    try { detail = (await response.json()).detail || detail } catch { /* ignore */ }
    throw new Error(detail)
  }
  return response.json()
}

/** Delete an object previously uploaded in this session (FilePond revert). */
export async function deleteUpload (token, key) {
  await fetch(`${API_BASE}/uploads/object`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, key })
  })
}

/**
 * Ask the API to email the contributor a review link. Returns the parsed
 * response ({ sent, review_url? }) or null on failure.
 */
export async function notifyCompletion (token, origin, locale) {
  try {
    const response = await fetch(`${API_BASE}/uploads/notify-completion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, origin, locale })
    })
    if (!response.ok) return null
    return await response.json()
  } catch (err) {
    console.error('[api] notifyCompletion failed:', err)
    return null
  }
}
